import {
  DayPickerStrings,
  FormikGraphQLDatePicker,
  onFormatDate,
} from 'components'
import { FormikBooleanChoiceGroup } from 'components/BooleanChoiceGroup/BooleanChoiceGroup'
import {
  FormikChoiceGroup,
  FormikDropdown,
  FormikTextField,
} from 'formik-office-ui-fabric-react'
import { FieldStack, FieldStackItem, FormStack } from 'forms/FormStack'
import { canUpdateProving, isInProvingRole } from 'helpers'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { ProvingUserRole } from 'model'
import {
  DayOfWeek,
  IChoiceGroupOption,
  IDropdownOption,
  Label,
  TextField,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

export class ProvingDetailsExternalVehicleFormSchema {
  constructor() {
    return Yup.object().shape({
      licensePlateType: Yup.string(),
      dateFrom: Yup.date().required(),
      dateTo: Yup.date()
        .required()
        .when(
          'dateFrom',
          (dateFrom: any, schema: any) => dateFrom && schema.min(dateFrom)
        ),
      orderId: Yup.string().required(),
    })
  }
}

export const ProvingDetailsExternalVehicleForm: React.FC<{
  invoked?: boolean
}> = ({ invoked }) => {
  const { t } = useTranslation()
  const [proving] = useCurrentProving()
  const orderIds = proving.orderIdDistribution
    ? proving.orderIdDistribution.map((oId) => oId)
    : []

  const [licensePlateKnown, setLicensePlateKnown] = React.useState()
  const [isUnknownVehicle, setIsUnknownVehicle] = React.useState(false)

  const licensePlateTypeOptions: IChoiceGroupOption[] = [
    {
      key: 'known',
      text: t('field.vehicle.known'),
      styles: { choiceFieldWrapper: { paddingRight: '10px' } },
    },
    {
      key: 'unknown',
      text: t('field.vehicle.unknown'),
      styles: { choiceFieldWrapper: { paddingRight: '10px' } },
    },
    { key: 'noRegistration', text: t('field.vehicle.noRegistration') },
  ]

  const [vehicleKnownOption, vehicleUnknownOption]: IChoiceGroupOption[] = [
    {
      key: 'known',
      text: t('field.vehicle.vehicleKnown'),
    },
    {
      key: 'unknown',
      text: t('field.vehicle.vehicleUnknown'),
    },
  ]

  const canUpdateCurrentProving = canUpdateProving(proving)
  const isMaintenanceHead = isInProvingRole(
    proving,
    ProvingUserRole.MaintenanceHead
  )
  const isProvingManager = isInProvingRole(
    proving,
    ProvingUserRole.ProvingManager
  )
  const userCanUpdate = () => {
    if (proving.status === 8 || proving.status === 9) {
      return false
    } else if (isMaintenanceHead) {
      return false
    } else if (canUpdateCurrentProving) {
      return true
    } else if (
      isProvingManager &&
      (proving.status === 1 ||
        proving.status === 4 ||
        proving.status === 5 ||
        proving.status === 10)
    ) {
      return true
    } else {
      return false
    }
  }

  return (
    <FormStack>
      {invoked && <Label>{t('field.vehicle.addExternalVehicle')}</Label>}
      <FieldStack>
        <FieldStackItem
          name="unknown"
          render={(fieldProps) => {
            setIsUnknownVehicle(fieldProps.field.value)
            return (
              <FormikBooleanChoiceGroup
                trueOption={vehicleUnknownOption}
                falseOption={vehicleKnownOption}
                falseOptionFirst={true}
                horizontal={true}
                fieldProps={fieldProps}
                disabled={!userCanUpdate()}
              />
            )
          }}
        />
      </FieldStack>
      <FieldStack>
        <FieldStackItem
          name="labelExternal"
          label={t('field.vehicle.labelExternalVehicle')}
          component={FormikTextField}
          disabled={!userCanUpdate()}
        />
      </FieldStack>

      {!isUnknownVehicle && (
        <>
          <FieldStack>
            <FieldStack>
              <FieldStackItem
                name="licensePlateType"
                render={(fieldProps) => {
                  setLicensePlateKnown(fieldProps.field.value === 'known')
                  return (
                    <FormikChoiceGroup
                      {...fieldProps}
                      label={t('field.vehicle.licensePlate.label')}
                      styles={{ flexContainer: { display: 'flex' } }}
                      options={licensePlateTypeOptions}
                      defaultSelectedKey={fieldProps.field.value}
                      disabled={!userCanUpdate()}
                    />
                  )
                }}
              />
              {licensePlateKnown && (
                <FieldStackItem
                  name="licensePlate"
                  component={FormikTextField}
                  disabled={!userCanUpdate()}
                />
              )}
            </FieldStack>
          </FieldStack>
          <FieldStack>
            <FieldStack>
              <FieldStackItem
                name="vin"
                label={t('field.vehicle.vin')}
                component={FormikTextField}
                disabled={!userCanUpdate()}
              />
            </FieldStack>
          </FieldStack>
        </>
      )}
      {isUnknownVehicle && (
        <TextField
          readOnly={true}
          label={t('field.vehicle.licensePlate.label')}
          value={t('field.vehicle.licensePlate.value')}
        />
      )}

      <FieldStack horizontal={true} wrap={true}>
        <FieldStackItem
          name="dateFrom"
          label={t('field.dateFrom.label')}
          isRequired={true}
          value={proving.dateFrom}
          component={FormikGraphQLDatePicker}
          // minDate={new Date(proving.dateFrom)}
          // maxDate={new Date(proving.dateTo)}
          firstDayOfWeek={DayOfWeek.Monday}
          strings={DayPickerStrings}
          showWeekNumbers={true}
          formatDate={onFormatDate}
          disabled={!userCanUpdate()}
          initialPickerDate={new Date(proving.dateFrom)}
        />
        <FieldStackItem
          name="dateTo"
          label={t('field.dateTo.label')}
          isRequired={true}
          component={FormikGraphQLDatePicker}
          // minDate={new Date(proving.dateFrom)}
          // maxDate={new Date(proving.dateTo)}
          firstDayOfWeek={DayOfWeek.Monday}
          strings={DayPickerStrings}
          showWeekNumbers={true}
          formatDate={onFormatDate}
          disabled={!userCanUpdate()}
          initialPickerDate={new Date(proving.dateFrom)}
        />
      </FieldStack>
      <FieldStack>
        <Label required={true}>{t('field.orderId.label')}</Label>
        <FieldStackItem
          name="orderId"
          label={t('field.orderId.label')}
          isRequired={true}
          render={(fieldProps) => (
            <FormikDropdown
              {...fieldProps}
              disabled={!userCanUpdate()}
              options={[
                { key: '', text: t('field.vehicle.noOrderId') },
                ...orderIds.map(
                  (orderId) =>
                    ({
                      key: orderId.orderId,
                      text: `${orderId.orderId} - ${
                        orderId.annotation ? `${orderId.annotation} ` : ''
                      }(${orderId.percentage} %)`,
                    } as IDropdownOption)
                ),
              ]}
            />
          )}
        />
      </FieldStack>
    </FormStack>
  )
}
